import { useRoute, Link } from "wouter";
import { Calendar, User, Tag, Clock, ArrowLeft, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import type { CaseStudy } from "@shared/blog-schema";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CASE_STUDY_FAQS } from "@/data/case-study-faqs";

function sanitizeCaseStudyContent(html: string): string {
  return html
    .replace(/<h1([^>]*)>/gi, '<h2$1>')
    .replace(/<\/h1>/gi, '</h2>')
    .replace(/<p><br\s*\/?><\/p>/gi, '')
    .trim();
}

export default function CaseStudyPage() {
  const [, params] = useRoute("/case-studies/:slug");
  const slug = params?.slug;
  const faqs = slug ? CASE_STUDY_FAQS[slug] : undefined;
  const { data: caseStudy, isLoading } = useQuery<CaseStudy>({
    queryKey: ['/api/case-studies', slug],
    queryFn: async () => {
      const res = await fetch(`/api/case-studies/${slug}`);
      if (!res.ok) throw new Error('Not found');
      return res.json();
    },
    enabled: !!slug,
    retry: false,
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatReadTime = (content: string) => {
    const wordsPerMinute = 200;
    const wordCount = content.split(" ").length;
    const readTime = Math.ceil(wordCount / wordsPerMinute);
    return readTime;
  };

  const handleShare = async () => {
    if (navigator.share && caseStudy) {
      try {
        await navigator.share({
          title: caseStudy.title,
          text: caseStudy.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        console.log("Error sharing:", err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading case study...</p>
        </div>
      </div>
    );
  }

  if (!caseStudy) {
    return (
      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center py-16">
            <div className="text-6xl mb-6">📄</div>
            <h1 className="text-2xl font-semibold mb-4">Case Study Not Found</h1>
            <p className="text-gray-600 mb-8">
              The case study you're looking for doesn't exist or has been removed.
            </p>
            <Button asChild>
              <Link href="/case-studies">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{caseStudy.metaTitle ?? caseStudy.title}</title>
        <meta name="description" content={caseStudy.metaDescription ?? caseStudy.excerpt} />
        <meta name="keywords" content={caseStudy.tags?.join(", ") || "SaaS Case Studies, SaaS Cost Optimization, License Management"} />
        <link rel="canonical" href={`https://saasgama.com/case-studies/${caseStudy.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="SaaSGama" />
        <meta property="og:url" content={`https://saasgama.com/case-studies/${caseStudy.slug}`} />
        <meta property="og:title" content={caseStudy.metaTitle ?? caseStudy.title} />
        <meta property="og:description" content={caseStudy.metaDescription ?? caseStudy.excerpt} />
        <meta property="og:image" content="https://saasgama.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@saasgama" />
        <meta name="twitter:title" content={caseStudy.metaTitle ?? caseStudy.title} />
        <meta name="twitter:description" content={caseStudy.metaDescription ?? caseStudy.excerpt} />
        <meta name="twitter:image" content="https://saasgama.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              "@id": `https://saasgama.com/case-studies/${caseStudy.slug}/#article`,
              "headline": caseStudy.title,
              "description": caseStudy.excerpt,
              "image": ["https://saasgama.com/case-study-image.jpg"],
              "author": { "@type": "Organization", "name": "SaaSGama" },
              "publisher": { "@type": "Organization", "name": "SaaSGama", "logo": { "@type": "ImageObject", "url": "https://saasgama.com/logo.png" } },
              "datePublished": caseStudy.publishedAt,
              "dateModified": caseStudy.updatedAt,
              "mainEntityOfPage": { "@type": "WebPage", "@id": `https://saasgama.com/case-studies/${caseStudy.slug}` },
              "articleSection": "Case Study",
              "keywords": caseStudy.tags || []
            },
            { "@type": "BreadcrumbList", "@id": `https://saasgama.com/case-studies/${caseStudy.slug}/#breadcrumb`, "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://saasgama.com" },
              { "@type": "ListItem", "position": 2, "name": "Case Studies", "item": "https://saasgama.com/case-studies" },
              { "@type": "ListItem", "position": 3, "name": caseStudy.title, "item": `https://saasgama.com/case-studies/${caseStudy.slug}` }
            ]},
            ...(faqs ? [{
              "@type": "FAQPage",
              "@id": `https://saasgama.com/case-studies/${caseStudy.slug}/#faq`,
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.q,
                "acceptedAnswer": { "@type": "Answer", "text": faq.a }
              }))
            }] : [])
          ]
        })}</script>
      </Helmet>

      {/* Back Button */}
      <div className="bg-gray-50 border-b">
        <div className="container mx-auto px-4 py-4">
          <Button variant="ghost" asChild>
            <Link href="/case-studies">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Case Studies
            </Link>
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <article className="max-w-4xl mx-auto">
          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              {caseStudy.tags && caseStudy.tags.map((tag: string) => (
                <Badge key={tag} variant="outline" className="text-xs">
                  <Tag className="h-3 w-3 mr-1" />
                  {tag}
                </Badge>
              ))}
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              {caseStudy.title}
            </h1>

            <p className="text-xl text-gray-600 mb-6 leading-relaxed">
              {caseStudy.excerpt}
            </p>

            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-6">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>{caseStudy.authorName}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{formatDate((caseStudy.publishedAt ?? caseStudy.createdAt).toString())}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>{formatReadTime(caseStudy.content)} min read</span>
              </div>
            </div>

            <Button onClick={handleShare} variant="outline" size="sm">
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </Button>
          </header>

          <div
            className="prose prose-lg max-w-none text-gray-800 leading-relaxed
              prose-h2:text-2xl prose-h2:font-bold prose-h2:text-gray-900 prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-xl prose-h3:font-semibold prose-h3:text-gray-800 prose-h3:mt-6 prose-h3:mb-3
              prose-p:mb-4 prose-ul:mb-4 prose-ol:mb-4 prose-li:mb-1"
            dangerouslySetInnerHTML={{ __html: sanitizeCaseStudyContent(caseStudy.content) }}
          />

          {/* FAQ Section */}
          {faqs && (
            <div className="mt-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible className="w-full space-y-3">
                {faqs.map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border border-gray-200 rounded-lg px-5 bg-white">
                    <AccordionTrigger className="text-left font-semibold text-gray-800 hover:text-blue-600">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 pt-1">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                {caseStudy.authorName.charAt(0)}
              </div>
              <div>
                <p className="font-semibold">{caseStudy.authorName}</p>
                <p className="text-gray-600 text-sm">{caseStudy.authorEmail === "team@saasgama.com" ? "support@saasgama.com" : caseStudy.authorEmail}</p>
              </div>
            </div>
          </div>

          <div className="mt-12 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Ready to Start Your Success Story?</h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Join hundreds of organizations that have transformed their software management with SaaSGama.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg">
                <Link href="/register">Get Your Trial Today</Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link href="/contact">Contact Sales</Link>
              </Button>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}
